import type { TenantBranding } from '@platform/contracts';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@platform/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, ImageIcon } from 'lucide-react';
import { type SubmitEvent, useId } from 'react';
import { toast } from 'sonner';

import { api, errorMessage } from '../lib/api';
import { fd } from './form';
import { ImagePicker } from './image-picker';
import { Loadable } from './states';

/**
 * Config → Marca: el logo de la empresa (el que se ve en el selector, los chips y el inicio) y su
 * web. Sin logo subido se usa el ícono de la web; sin web, el monograma.
 */
export function BrandingTab({
  tenantId,
  tenantName,
  tenantSlug,
  canWrite,
}: {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  canWrite: boolean;
}) {
  const qc = useQueryClient();
  const webId = useId();
  const key = ['branding', tenantId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<TenantBranding>(`/api/tenants/${tenantId}/branding`),
  });
  const saved = async (r: TenantBranding, msg: string) => {
    qc.setQueryData(key, r);
    await qc.invalidateQueries({ queryKey: ['me'] });
    toast.success(msg);
  };
  const website = useMutation({
    mutationFn: (value: string) =>
      api<TenantBranding>(`/api/tenants/${tenantId}/website`, {
        method: 'PUT',
        body: { website: value || null },
      }),
    onSuccess: (r) => saved(r, r.website ? 'Web guardada' : 'Web quitada'),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const onSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    website.mutate(fd(new FormData(e.currentTarget), 'website'));
  };
  const b = q.data;
  return (
    <Loadable isLoading={q.isLoading} error={q.error} onRetry={() => void q.refetch()}>
      {b ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle icon={<ImageIcon />}>Logo</CardTitle>
              <CardDescription>
                Aparece en el selector de empresa, en las conversaciones y en el inicio.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ImagePicker
                name={tenantName}
                colorKey={tenantSlug}
                src={b.logoUrl}
                kind="logo"
                hasImage={b.hasUploadedLogo}
                disabled={!canWrite}
                hint={
                  b.hasUploadedLogo
                    ? 'PNG con fondo transparente queda mejor. Máximo 512 KB.'
                    : b.website
                      ? `Ahora se usa el ícono de ${b.website}. Sube un logo para reemplazarlo.`
                      : 'Sin logo ni web se muestran las iniciales.'
                }
                onUpload={async (dataUrl) => {
                  const r = await api<TenantBranding>(`/api/tenants/${tenantId}/logo`, {
                    method: 'PUT',
                    body: { contentBase64: dataUrl },
                  });
                  await saved(r, 'Logo actualizado');
                }}
                onRemove={async () => {
                  const r = await api<TenantBranding>(`/api/tenants/${tenantId}/logo`, {
                    method: 'DELETE',
                  });
                  await saved(r, 'Logo quitado');
                }}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle icon={<Globe />}>Web de la empresa</CardTitle>
              <CardDescription>De aquí sale el ícono cuando no hay un logo subido.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="flex flex-col gap-2">
                <Label htmlFor={webId}>Dominio</Label>
                <div className="flex gap-2">
                  <Input
                    id={webId}
                    name="website"
                    key={b.website ?? ''}
                    defaultValue={b.website ?? ''}
                    placeholder="tienda.es"
                    inputMode="url"
                    autoComplete="url"
                    disabled={!canWrite}
                  />
                  <Button type="submit" variant="outline" disabled={!canWrite || website.isPending}>
                    Guardar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </Loadable>
  );
}
